import { useEffect, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Layout from "@wrappers/layouts/main-layout/Layout";
import { useClearGridData, useGetModulesData, usePermissionCheck } from "@hooks/AllCustomHooks";
import { mainContext } from "@ts/constants";
import { createDatasource } from "@ts/retrieve-data";
import {
    getActiveModule,
    agBtnExport,
    resetPillBadges,
    setPillBadges,
} from "@ts/utils";
import { getDateObj } from "@ts/date-range";
import { setIsLoader } from "@features/loaderSlice";
import { updateTitle } from "@features/titleSlice";
import { updateActiveModule } from "@features/activeModuleSlice";
import AddRulesModal from "@modals/rules-modal/AddRulesModal";
import * as Sentry from "@sentry/react";
import {
    IChannel,
    IMainContext,
    Keyword,
    ParentModule,
    Platforms,
    SubModule,
    TActiveClientId,
    TCommonRowData,
} from "@ts/common-types";
import { filter } from "underscore";
import {
    setExistingNegKwsData,
    setExistingNegKwsLoading,
    setSuggestedNegKwsData,
    setSuggestedNegKwsLoading,
} from "@/features/gridDataSlice";
import TabsComponent from "@/components/tabs/TabsComponent";
import SuggestedNegativeKeywords from "./SuggestedNegativeKeywords";
import { useLocation } from "react-router-dom";
import { updateActiveTab } from "@/features/activeTabSlice";

const NegativeKeywords = () => {
    const clearGridData = useClearGridData();
    const activeClientId = useSelector(
        (state: TActiveClientId) => state.activeClientId.value
    );
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const {
        dates,
        DateRange,
        suggestedNegKwsGridApi,
        existingNegKwsGridApi,
    } = useContext<IMainContext>(mainContext);
    const loaderCount = useGetModulesData({ type: "loader-count" });

    let loader = 0;

    const incrementLoader = () => {
        loader = loader + 1;
        if (loader === loaderCount) {
            dispatch(setIsLoader(false));
        }
    };
    const channel = useSelector((state: IChannel) => state.channel.value);

    const { activeModule } = getActiveModule();

    const populateSuggestedNegativeKeywords = async () => {
        return await createDatasource("suggested-negative-keywords", {
            dateObj: getDateObj(),
        });
    };

    const populateExistingNegativeKeywords = async () => {
        return await createDatasource("existing-negative-keywords", {
            dateObj: getDateObj(),
        });
    };

    const onBtnExport = () => {
        if (activeModule === SubModule.SUGGESTED_NEG_KWS) {
            agBtnExport(suggestedNegKwsGridApi);
        } else if (activeModule === SubModule.EXISTING_NEG_KWS) {
            agBtnExport(existingNegKwsGridApi);
        }
    };

    useEffect(() => {
        clearGridData();
        dispatch(updateTitle("Negative Keywords"));
        dispatch(updateActiveTab("suggested"));
        dispatch(updateActiveModule("suggested"));
        dispatch(setIsLoader(true));
        dispatch(setSuggestedNegKwsLoading(true));
        dispatch(setExistingNegKwsLoading(true));
        dispatch(setSuggestedNegKwsData(null));
        dispatch(setExistingNegKwsData(null));
        // eslint-disable-next-line
        loader = 0;
        resetPillBadges();

        populateSuggestedNegativeKeywords()
            .then((keywords) => {
                setPillBadges(
                    "suggested-badge",
                    (keywords as TCommonRowData).length
                );
                dispatch(
                    setSuggestedNegKwsData(
                        filter(
                            keywords,
                            (keyword: Keyword) => !keyword.negative
                        )
                    )
                );
                dispatch(setSuggestedNegKwsLoading(false));
                incrementLoader();
            })
            .catch((err) => {
                console.error(err);
                Sentry.captureException(err);
            });

        populateExistingNegativeKeywords()
            .then((keywords) => {
                setPillBadges(
                    "existing-badge",
                    (keywords as TCommonRowData).length
                );
                dispatch(setExistingNegKwsData(keywords as Keyword[]));
                dispatch(setExistingNegKwsLoading(false));
                incrementLoader();
            })
            .catch((err) => {
                console.error(err);
                Sentry.captureException(err);
            });
        // eslint-disable-next-line
    }, [activeClientId, dates, DateRange, dispatch, channel]);

    const { canCreateRule } = usePermissionCheck();

    return (
        <>
            <Layout>
                <div className="main-content-body subheader-fixed">
                    <div className="tabbable-line">
                        <TabsComponent onBtnExport={onBtnExport} />
                        {pathname ===
                            `/${Platforms.ACTIVATION}/${ParentModule.NEGATIVE_KEYWORDS}` && (
                            <SuggestedNegativeKeywords />
                        )}
                    </div>
                </div>
                <AddRulesModal
                    api={suggestedNegKwsGridApi}
                    canAddRule={canCreateRule}
                />
            </Layout>
        </>
    );
};

export default NegativeKeywords;
